import { cloneDeep } from 'lodash';
import { v4 as uuid } from 'uuid';
import { enhanceBridge, enhanceGLBridge, fixZipResUrl, maskSmearBridge } from '@meitu/wasm-lib';
import assetsConfig from '@meitu/wasm-lib/assetsConfig.json';
import { MTIKMaskSmearFilter } from '@meitu/wasm-lib/typings/lib/filter/MTIKMaskSmearFilter';
import { getFramework, IFramework, trackConfig } from '@/framework';
import { applyEnhanceGLResult } from '@/layers/main-layer/actions/enhance';
import { base64Kit } from '@/utils/base64-kit';
import { createCanvas, getImgInfoAsync } from '@/utils/file-kit';
import { createFromCanvas } from '@/utils/layer-kit';
import { IImageLayerProps, IImageProps, ILayerProps } from '@/version-types';
import { ALL_COLOR_LIST, COLOR_LIST, FILTER_MASK_COLOR, MASK_COLOR, PREVIEW_SIZE, SMEAR_PEN_SIZE } from './constants';
import { getIntelligentColorState, IStore, watchIntelligentColorChange } from './states/intelligent-color-state';
import { IIntelligentEnhanceParams, IIntelligentPreviewItem, IMaskData, INTELLIGENT_COLOR_CHANGE_EVENT } from './types';
import {
    formatIntelligentColor,
    getColorParams,
    getGlParams,
    getHsl,
    getRatioImage,
    uint8arrayToCanvas,
} from './utils';

const assets = {
    id: 'Enhance',
    zip: assetsConfig.filter_Enhance,
};

const prevMaskInfo: { [key: string]: any } = {
    imageData: null,
    code: '',
};

export class IntelligentColorPlugin {
    /** 编辑器实例 */
    private framework: IFramework;
    private store: IStore;

    constructor(identity: string) {
        this.framework = getFramework(identity);
        this.store = getIntelligentColorState(identity);

        watchIntelligentColorChange(identity, (state) => {
            this.store = state;
        });
    }

    getStore() {
        return this.store;
    }

    watch(callback: Parameters<typeof watchIntelligentColorChange>['1']) {
        return watchIntelligentColorChange(this.framework.identity, callback);
    }

    async create(): Promise<HTMLCanvasElement | null> {
        const { framework } = this;
        const originIndex = framework.stage.getActiveOriginIndex();
        const { setOriginLayer } = this.store;
        const originLayer = framework.getMainLayer(originIndex)!;
        const newLayer = cloneDeep(originLayer);

        setOriginLayer(originLayer);

        const url = (newLayer.data as IImageLayerProps).url;
        const fromCanvas = await createFromCanvas(newLayer.id, url);
        const glCanvas = await enhanceGLBridge.from(fromCanvas).create(assets.id, fixZipResUrl(assets.zip));

        newLayer.__renderEnhanceCanvas = true;

        this.setHslMode(1);
        framework.stage.addOrUpdate(newLayer);
        this.store.setIsIntelligent(true);
        return glCanvas;
    }

    async destroy() {
        const { framework } = this;
        const originIndex = framework.stage.getActiveOriginIndex();
        const oldLayer = this.store.originLayer!;
        const layer = cloneDeep(framework.getMainLayer(originIndex)!);
        // 应用上一次gl的结果
        maskSmearBridge.enableRenderToView(false, false);
        const manager = await enhanceGLBridge.getManager();
        manager.processRender(true);
        await applyEnhanceGLResult(layer);
        framework.stage.addOrUpdate(layer);

        // 退出时，如果主图没有变化，则不写入历史记录
        if ((oldLayer.data as IImageLayerProps).url !== (layer.data as IImageLayerProps).url) {
            framework.history.write(oldLayer, layer, trackConfig.adjust.intelligentColor);
        }

        await this.reset();
        this.store.setIsIntelligent(false);
        this.store.setIsProtectPen(false);
    }

    async reset() {
        const scale = this.framework.stage.getScale();

        this.setHslMode(0);
        this.setSmearPenSize(SMEAR_PEN_SIZE / 2 / scale);
        this.stopSmear();
        this.store.intelligentDestroy();
        this.framework.events.emit(INTELLIGENT_COLOR_CHANGE_EVENT.Reset);
    }

    onReset(handleReset: () => void) {
        const handler = this.framework.events.on(INTELLIGENT_COLOR_CHANGE_EVENT.Reset, handleReset);

        return () => {
            this.framework.events.off(handler);
        };
    }

    resetColor() {
        this.framework.events.emit(INTELLIGENT_COLOR_CHANGE_EVENT.ResetColor);
    }

    onResetColor(handleReset: () => void) {
        const handler = this.framework.events.on(INTELLIGENT_COLOR_CHANGE_EVENT.ResetColor, handleReset);

        return () => {
            this.framework.events.off(handler);
        };
    }

    async restore() {
        const originLayer = this.store.originLayer!;

        await this.framework.stage.addOrUpdate(originLayer);
        this.reset();
        setTimeout(() => {
            this.create();
        }, 0);
    }

    async initIntelligentColors() {
        const colors = await enhanceGLBridge.imageDetec();
        this.store.initIntelligentColors(formatIntelligentColor(colors).slice(0, 5));
    }

    private async setHslMode(mode: number) {
        enhanceGLBridge.setHSLMode(mode);
        enhanceBridge.setHSLMode(mode);
    }

    private async createMask(maskData?: IMaskData) {
        const manager = await enhanceBridge.getManager();
        await maskSmearBridge.setManager(manager, false).create();
        if (maskData) {
            const { width, height, imagedata } = maskData;
            maskSmearBridge.setMaskData(imagedata, width, height);
        }
    }

    private async applyEnhance(layer: ILayerProps, data: IIntelligentEnhanceParams) {
        const url = (layer.data as IImageLayerProps).url;
        const params = getGlParams(data);

        const fromCanvas = await createFromCanvas(layer.id, url);
        await enhanceBridge.from(fromCanvas).doEnhance(assets.id, fixZipResUrl(assets.zip));
        this.setHslMode(1);
        if (data.maskCode) {
            await this.doMask(data.maskCode);
        }
        enhanceBridge.setParams(params);
        const { canvas } = await enhanceBridge.getGLResult();
        if (canvas) {
            const base64 = canvas.toDataURL();
            const data = layer.data as IImageProps;
            data.url = base64Kit.getCode(base64, uuid());
        }
        this.setHslMode(0);
        delete layer.__renderEnhanceCanvas;
        delete layer.__isDoEnhance;

        maskSmearBridge.destroy();
        this.framework.stage.addOrUpdate(layer);
    }

    private async applyGLEnhance(data: IIntelligentEnhanceParams) {
        const params = getGlParams(data);
        await enhanceGLBridge.setParams(params);
    }

    private async doGlMask(maskCode: string) {
        console.time('doGlMask');
        const manager = await enhanceGLBridge.getManager();
        const maskInstance = maskSmearBridge.getFilterInstance();
        if (!maskInstance) {
            await maskSmearBridge.setManager(manager, false).create();
        }
        await enhanceGLBridge.startSmear(maskSmearBridge.getFilterInstance() as MTIKMaskSmearFilter);
        this.setMaskColor();
        this.setFilterMaskColor();

        maskSmearBridge.enableRenderToView(false, false);
        manager.processRender(true);
        await this.setMaskData(maskCode || '');
        maskSmearBridge.enableRenderToView(true, true);
        manager.processRender(true);
        console.timeEnd('doGlMask');
    }

    private async doMask(maskCode: string) {
        console.time('doMask');
        const manager = await enhanceBridge.getManager();
        const maskInstance = maskSmearBridge.getFilterInstance();
        if (!maskInstance) {
            await maskSmearBridge.setManager(manager, false).create();
        }
        await enhanceBridge.startSmear(maskSmearBridge.getFilterInstance() as MTIKMaskSmearFilter);
        this.setMaskColor();
        this.setFilterMaskColor();

        maskSmearBridge.enableRenderToView(false, false);
        manager.processRender(true);
        await this.setMaskData(maskCode || '');
        manager.processRender(true);
        console.timeEnd('doMask');
    }

    async startSmear() {
        const scale = this.framework.stage.getScale();
        const glManager = await enhanceGLBridge.getManager();
        maskSmearBridge
            .setManager(glManager, false)
            .create()
            .then(() => {
                enhanceGLBridge.startSmear(maskSmearBridge.getFilterInstance() as MTIKMaskSmearFilter);
                this.setMaskColor();
                this.setSmearPenSize(SMEAR_PEN_SIZE / 2 / scale);
                this.setFilterMaskColor();
            });
    }

    async getMaskData() {
        console.time('getMaskData');
        const maskData = await maskSmearBridge.getMaskData();
        if (maskData) {
            const base64 = uint8arrayToCanvas(maskData);
            const code = await base64Kit.getCode(base64);
            console.timeEnd('getMaskData');
            return code;
        }
        return '';
    }

    private async setMaskData(code: string) {
        console.time('setMaskData');
        if (!code) return;
        if (code === prevMaskInfo.code) {
            const { imageData } = prevMaskInfo;
            const { data, width, height } = imageData;
            await maskSmearBridge.setMaskData(data, width, height);
            return;
        }
        const url = await base64Kit.getRemoteUrlAsync(code);
        const { width, height, img } = await getImgInfoAsync(url);
        const { ctx } = createCanvas(width, height, img);
        const data = ctx.getImageData(0, 0, width, height).data;
        await maskSmearBridge.setMaskData(data, width, height);
        prevMaskInfo.imageData = { data, width, height };
        prevMaskInfo.code = code;
        console.timeEnd('setMaskData');
    }

    private async setMaskColor() {
        enhanceGLBridge.setMaskColor(MASK_COLOR);
    }

    async setSmearPenSize(size: number) {
        enhanceGLBridge.setMaskPenSize(size);
    }

    async setFilterMaskColor() {
        const [r, g, b, a] = FILTER_MASK_COLOR;
        await maskSmearBridge.setMaskColor(r, g, b, a);
    }

    async setEraserType() {
        maskSmearBridge.setMaskSmearMode(0);
    }

    async setPenType() {
        maskSmearBridge.setMaskSmearMode(1);
    }

    async resetMask() {
        maskSmearBridge.reset();
    }

    async stopSmear() {
        const scale = this.framework.stage.getScale();
        this.setSmearPenSize(SMEAR_PEN_SIZE / 2 / scale);
        maskSmearBridge.destroy();
    }

    updateLayer() {
        const { framework } = this;
        const newLayer = cloneDeep(framework.getLayer(this.store.originLayer?.id!));

        newLayer.__isDoEnhance = true;
        framework.stage.addOrUpdate(newLayer);
    }

    async applyGLEnhanceAndUpdateLayer(data: IIntelligentEnhanceParams) {
        await this.applyGLEnhance(cloneDeep(data));
        this.updateLayer();
    }

    async getHSLFromColors(from: string, target: string) {
        const hslPickColors = await enhanceGLBridge.getHSLFromColors(
            from.split(',').map((v) => +v / 255),
            target.split(',').map((v) => +v / 255),
        );
        return hslPickColors;
    }

    private async getColorPreviewData(
        pickColors: string,
        hslPickColors: number[] | [],
        fromCanvas: HTMLCanvasElement,
        isHslSelectAll: boolean = false,
        color: any = {},
    ) {
        const params = getGlParams({ pickColors, hslPickColors, isHslSelectAll, color });

        enhanceBridge.setParams(params);
        const { canvas } = await enhanceBridge.getGLResult();
        return canvas?.toDataURL() || '';
    }

    async renderIntelligentPreview(color: string) {
        console.time('renderIntelligentPreview');
        const { framework } = this;
        const originIndex = framework.stage.getActiveOriginIndex();
        try {
            const tempArr: IIntelligentPreviewItem[] = [];
            const layer = framework.getMainLayer(originIndex)!;
            const data = layer.data as IImageLayerProps;
            if (!data.url) return;
            const canvas = await getRatioImage(data.url, PREVIEW_SIZE);
            await enhanceBridge.from(canvas).doEnhance(assets.id, fixZipResUrl(assets.zip));
            enhanceBridge.setHSLMode(1);
            const maskInstance = maskSmearBridge.getFilterInstance();
            if (maskInstance) {
                enhanceBridge.startSmear(maskInstance);
            }
            maskSmearBridge.enableRenderToView(false, false);

            for (const item of COLOR_LIST) {
                const hslPickColors = await this.getHSLFromColors(color, item);
                const base64 = await this.getColorPreviewData(color, hslPickColors, canvas);
                tempArr.push({
                    url: base64,
                    hslPickColors,
                    pickColors: color,
                    color: item,
                    fuzziness: 0,
                });
            }

            tempArr.unshift({
                url: canvas.toDataURL(),
                hslPickColors: [0, 0, 0],
                pickColors: '',
                color: '',
                fuzziness: 0,
            });
            this.store.setIntelligentPreviewList(tempArr);
            maskSmearBridge.enableRenderToView(true, false);
            await enhanceBridge.stopSmear(maskSmearBridge.getFilterInstance() as MTIKMaskSmearFilter);
            enhanceBridge.destroy();
        } catch (e) {
            console.log('renderIntelligentPreview error ', e);
        }
        console.timeEnd('renderIntelligentPreview');
    }

    async renderAllIntelligentPreview(maskCode?: string) {
        console.time('renderAllIntelligentPreview');
        const { framework } = this;
        const originIndex = framework.stage.getActiveOriginIndex();
        try {
            const tempArr: IIntelligentPreviewItem[] = [];
            const layer = framework.getMainLayer(originIndex)!;
            const data = layer.data as IImageLayerProps;
            if (!data.url) return;
            const canvas = await getRatioImage(data.url, PREVIEW_SIZE);
            await enhanceBridge.from(canvas).doEnhance(assets.id, fixZipResUrl(assets.zip));
            enhanceBridge.setHSLMode(1);
            const maskInstance = maskSmearBridge.getFilterInstance();
            if (maskInstance) {
                enhanceBridge.startSmear(maskInstance);
            }
            if (maskCode) {
                await this.setMaskData(maskCode);
            } else if (maskCode === '') {
                await this.resetMask();
            }
            maskSmearBridge.enableRenderToView(false, false);

            for (const item of ALL_COLOR_LIST) {
                const color = getColorParams(item);
                const hslPickColors = getHsl(item);
                const base64 = await this.getColorPreviewData('', [], canvas, true, color);
                tempArr.push({
                    url: base64,
                    hslPickColors,
                    pickColors: '',
                    color: item,
                });
            }

            tempArr.unshift({
                url: canvas.toDataURL(),
                hslPickColors: [0, 0, 0],
                pickColors: '',
                color: '',
            });
            this.store.setIntelligentPreviewList(tempArr);

            maskSmearBridge.enableRenderToView(true, false);

            await enhanceBridge.stopSmear(maskSmearBridge.getFilterInstance() as MTIKMaskSmearFilter);
            enhanceBridge.destroy();
        } catch (e) {
            console.log('renderAllIntelligentPreview error ', e);
        }
        console.timeEnd('renderAllIntelligentPreview');
    }
}
